<template>
    <div class="page parent-report">
        <v-container class="py-4 py-lg-8 px-4 report-container">

            <!-- feature off -->
            <v-card v-if="!enabled" flat class="form-wrapper soft-shadow text-center pa-6">
                <v-icon size="44" color="grey">mdi-file-lock-outline</v-icon>
                <div class="strong-text--text mt-4 state-text">هذه الصفحة غير متاحة حاليًا</div>
            </v-card>

            <!-- trying a stored link / loading the report -->
            <div v-else-if="state === 'loading'">
                <v-skeleton-loader type="card-heading, list-item-two-line" class="mb-4" />
                <v-row dense>
                    <v-col v-for="i in 4" :key="i" cols="6" md="3">
                        <v-skeleton-loader type="image" height="96" />
                    </v-col>
                </v-row>
                <v-skeleton-loader type="list-item-three-line, list-item-three-line" class="mt-4" />
            </div>

            <!-- link no longer valid -->
            <v-card v-else-if="state === 'invalid'" flat class="form-wrapper soft-shadow text-center pa-6">
                <v-icon size="44" color="error">mdi-link-variant-off</v-icon>
                <div class="strong-text--text mt-4 state-text">هذا الرابط لم يعد صالحًا. تواصل مع إدارة المنصة للحصول على رابط جديد</div>
            </v-card>

            <!-- code form -->
            <v-card v-else-if="state === 'form'" flat class="form-wrapper soft-shadow pb-6">
                <v-form @submit.prevent="verify">
                    <v-card-title class="justify-center mb-1">تقرير متابعة الطالب</v-card-title>
                    <v-card-subtitle class="grey--text details-text text-center mb-5 mx-2">أدخل الرمز المكوّن من 6 أرقام الذي وصلك مع الرابط</v-card-subtitle>
                    <v-text-field
                        v-model="code"
                        class="code-input mx-2"
                        outlined
                        type="tel"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        placeholder="000000"
                        dir="ltr"
                        :disabled="verifying"
                        :error-messages="codeError ? [codeError] : []"
                        @input="onCodeInput"
                    />
                    <div class="px-2">
                        <v-btn type="submit" block depressed x-large color="primary" :disabled="code.length !== 6 || verifying" :loading="verifying">عرض التقرير</v-btn>
                    </div>
                </v-form>
            </v-card>

            <!-- report -->
            <div v-else-if="state === 'report' && report">
                <!-- a. header -->
                <v-card flat class="soft-shadow rounded-lg pa-4 mb-4">
                    <div class="d-flex align-center">
                        <v-avatar color="primary" size="48" class="ml-3">
                            <v-icon dark>mdi-account-school-outline</v-icon>
                        </v-avatar>
                        <div class="min-w-0">
                            <div class="text-h6 font-weight-bold strong-text--text student-name">{{ report.student.name }}</div>
                            <div v-if="report.student.grade" class="grey--text text--darken-1">{{ report.student.grade }}</div>
                        </div>
                    </div>
                    <div class="mt-3 grey--text text--darken-2 details-text">
                        الفترة: من <span dir="ltr" class="d-inline-block num">{{ report.period.from }}</span> إلى <span dir="ltr" class="d-inline-block num">{{ report.period.to }}</span>
                    </div>
                </v-card>

                <!-- b. stat tiles -->
                <v-row dense class="mb-3">
                    <v-col v-for="tile in tiles" :key="tile.label" cols="6" md="3">
                        <v-card flat class="bordered stat-tile pa-3 fill-height">
                            <div class="d-flex flex-column align-center text-center">
                                <v-icon size="26" :color="tile.color">{{ tile.icon }}</v-icon>
                                <div class="stat-value num strong-text--text mt-1">{{ tile.value }}</div>
                                <div class="details-text grey--text text--darken-1">{{ tile.label }}</div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- c. overall -->
                <v-card flat class="soft-shadow rounded-lg pa-4 mb-4">
                    <div class="section-title strong-text--text mb-3">الأداء العام</div>
                    <div class="d-flex align-center flex-wrap">
                        <div v-if="report.overall.average !== null && report.overall.average !== undefined" class="average-value num strong-text--text ml-3" dir="ltr">{{ formatPct(report.overall.average) }}%</div>
                        <div v-else class="grey--text text--darken-1 ml-3">لا توجد نتائج بعد</div>
                        <v-chip v-if="band" :color="band.color" text-color="white" small label class="font-weight-bold">{{ band.text }}</v-chip>
                    </div>
                    <div class="mt-3 grey--text text--darken-2 details-text">
                        <v-icon small class="ml-1">mdi-clock-outline</v-icon>
                        <template v-if="report.overall.last_activity_at">آخر نشاط: <span dir="ltr" class="d-inline-block num">{{ report.overall.last_activity_at }}</span></template>
                        <template v-else>لا يوجد نشاط مسجل</template>
                    </div>
                </v-card>

                <!-- d. attention -->
                <v-card flat class="soft-shadow rounded-lg pa-4 mb-4">
                    <div class="section-title strong-text--text mb-3">ما يحتاج متابعة</div>
                    <div v-if="!attentionItems.length" class="d-flex align-center success--text">
                        <v-icon color="success" class="ml-2">mdi-check-circle-outline</v-icon>
                        <span>لا يوجد واجب متأخر أو متطلب دراسي ناقص حاليًا</span>
                    </div>
                    <template v-else>
                        <v-alert v-for="(item, i) in attentionItems" :key="i" dense text :color="alertColor(item.type)" :icon="attentionIcon(item.type)" class="mb-2">
                            <div>{{ item.text }}</div>
                            <div v-if="item.course" class="caption">{{ item.course }}</div>
                        </v-alert>
                    </template>
                </v-card>

                <!-- e. week results -->
                <v-card flat class="soft-shadow rounded-lg pa-4 mb-4">
                    <div class="section-title strong-text--text mb-2">نتائج الامتحانات والواجبات في آخر 7 أيام</div>
                    <div v-if="!weekResults.length" class="grey--text text--darken-1 py-2">لا توجد امتحانات أو واجبات هذا الأسبوع</div>
                    <div v-else>
                        <div v-for="(r, i) in weekResults" :key="i" class="result-row py-3" :class="{'with-border': i > 0}">
                            <div class="font-weight-medium strong-text--text">{{ r.name }}</div>
                            <div class="caption grey--text text--darken-1">
                                {{ r.course }}<template v-if="r.course && r.date"> · </template><span v-if="r.date" dir="ltr" class="d-inline-block num">{{ r.date }}</span>
                            </div>
                            <div class="mt-2 d-flex flex-wrap chips-gap">
                                <v-chip small outlined>{{ r.type === 'homework' ? 'واجب' : 'امتحان' }}</v-chip>
                                <v-chip small :color="statusChip(r).color" text-color="white">{{ statusChip(r).text }}<span v-if="statusChip(r).pct !== null" dir="ltr" class="num mr-1">{{ statusChip(r).pct }}%</span></v-chip>
                            </div>
                        </div>
                    </div>
                </v-card>

                <!-- f. courses -->
                <v-card flat class="soft-shadow rounded-lg pa-4 mb-4">
                    <div class="section-title strong-text--text mb-3">تفاصيل الكورسات</div>
                    <div v-if="!courses.length" class="grey--text text--darken-1 py-2">لا توجد كورسات مسجلة</div>
                    <v-expansion-panels v-else accordion flat class="bordered rounded">
                        <v-expansion-panel v-for="(c, i) in courses" :key="i">
                            <v-expansion-panel-header class="course-header">
                                <div class="d-flex flex-column min-w-0">
                                    <span class="font-weight-medium strong-text--text">{{ c.name }}</span>
                                    <span class="caption grey--text text--darken-1 mt-1"><span class="num" dir="ltr">{{ c.lectures_seen || 0 }}/{{ c.lectures_total || 0 }}</span> محاضرات</span>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <div v-for="line in courseLines(c)" :key="line.label" class="mb-3">
                                    <div class="d-flex justify-space-between details-text mb-1">
                                        <span>{{ line.label }}</span>
                                        <span dir="ltr" class="num">{{ line.done }}/{{ line.total }}</span>
                                    </div>
                                    <v-progress-linear :value="line.total ? (line.done / line.total) * 100 : 0" height="8" rounded color="primary" background-color="grey lighten-3" />
                                </div>
                                <v-alert v-for="(a, j) in (c.attention || [])" :key="'a' + j" dense text :color="alertColor(a.type)" :icon="attentionIcon(a.type)" class="mb-2">
                                    {{ a.text }}
                                </v-alert>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card>

                <!-- g. footer -->
                <div class="text-center pb-6">
                    <div class="caption grey--text text--darken-1">التقرير يعرض آخر 7 أيام مكتملة</div>
                    <v-btn text small color="grey darken-1" class="mt-2 logout-btn" @click="logout">تسجيل الخروج من التقرير</v-btn>
                </div>
            </div>

        </v-container>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    data(){
        return {
            state: 'loading', // loading | form | invalid | report
            code: '',
            codeError: '',
            verifying: false,
            report: null,
        }
    },
    computed: {
        ...GlobalComputed,
        enabled(){
            return !!(this.appData && this.appData.enable_parent_report);
        },
        token(){
            return this.$route.params.token;
        },
        storageKey(){
            return 'parent_report_' + this.token;
        },
        tiles(){
            const w = (this.report && this.report.week) || {};
            return [
                {label: 'أيام النشاط', value: w.active_days || 0, icon: 'mdi-calendar-check-outline', color: 'primary'},
                {label: 'فيديوهات بدأها', value: w.videos_started || 0, icon: 'mdi-play-circle-outline', color: 'indigo'},
                {label: 'واجبات سلّمها', value: w.homeworks_submitted || 0, icon: 'mdi-book-check-outline', color: 'teal'},
                {label: 'امتحانات أدّاها', value: (w.results || []).filter(r => r.type === 'exam').length, icon: 'mdi-clipboard-text-outline', color: 'orange'},
            ];
        },
        band(){
            const bands = {
                excellent: {text: 'ممتاز', color: 'success'},
                good: {text: 'جيد', color: 'teal'},
                review: {text: 'يحتاج مراجعة', color: 'orange'},
                support: {text: 'يحتاج دعم', color: 'error'},
            };
            return bands[this.report && this.report.overall && this.report.overall.band] || null;
        },
        courses(){
            return (this.report && this.report.courses) || [];
        },
        weekResults(){
            return (this.report && this.report.week && this.report.week.results) || [];
        },
        attentionItems(){
            const items = [];
            this.courses.forEach(c => (c.attention || []).forEach(a => items.push({...a, course: c.name})));
            return items;
        },
    },
    beforeDestroy(){
        document.documentElement.classList.remove('parent-report-mode');
    },
    methods: {
        ...GlobalMethods,
        onCodeInput(value){
            // Arabic-Indic and Persian digits are common on Arabic keyboards; map them to 0-9 first.
            const digits = String(value || '')
                .replace(/[\u0660-\u0669]/g, d => d.charCodeAt(0) - 0x0660)
                .replace(/[\u06F0-\u06F9]/g, d => d.charCodeAt(0) - 0x06F0)
                .replace(/\D/g, '').slice(0, 6);
            this.codeError = '';
            if (digits !== value){
                this.$nextTick(() => { this.code = digits; });
            }
        },
        verify(){
            if (this.code.length !== 6 || this.verifying) return;
            this.verifying = true;
            this.codeError = '';
            window.axios.post(`/parent/${encodeURIComponent(this.token)}/verify`, {code: this.code}).then(response => {
                const target = response.data && response.data.url; // absolute, so axios ignores baseURL
                if (!target){
                    this.verifying = false;
                    this.codeError = 'حدث خطأ، حاول مرة أخرى';
                    return;
                }
                try { window.localStorage.setItem(this.storageKey, target); } catch (e) { /* storage blocked */ }
                this.loadReport(target);
            }).catch(error => {
                this.verifying = false;
                const status = error && error.response && error.response.status;
                if (status === 404){
                    this.state = 'invalid';
                }else if (status === 422){
                    this.codeError = (error.response.data && error.response.data.message) || 'الرمز غير صحيح';
                }else if (status === 429){
                    this.codeError = 'محاولات كثيرة، حاول بعد دقيقة';
                }else{
                    this.codeError = 'تعذر الاتصال، حاول مرة أخرى';
                }
            });
        },
        loadReport(target){
            this.state = 'loading';
            window.axios.get(target).then(response => {
                this.report = response.data;
                this.code = '';
                this.verifying = false;
                this.state = 'report';
            }).catch(error => {
                this.verifying = false;
                const status = error && error.response && error.response.status;
                if (status === 403 || status === 404){
                    this.forget(); // expired, code reset, or link replaced/disabled
                    this.state = status === 404 ? 'invalid' : 'form';
                }else{
                    this.report = null;
                    this.state = 'form'; // network hiccup: keep the saved access, ask for the code this time
                }
            });
        },
        // 204 = link exists, 404 = replaced, disabled or feature off. Nothing about the student is returned.
        checkLink(){
            window.axios.get(`/parent/${encodeURIComponent(this.token)}`).then(() => {
                this.state = 'form';
            }).catch(error => {
                const status = error && error.response && error.response.status;
                this.state = status === 404 ? 'invalid' : 'form';
            });
        },
        forget(){
            try { window.localStorage.removeItem(this.storageKey); } catch (e) { /* storage blocked */ }
            this.report = null;
        },
        logout(){
            this.forget();
            this.code = '';
            this.codeError = '';
            this.state = 'form';
        },
        formatPct(n){
            if (n === null || n === undefined || n === '') return null;
            const v = Number(n);
            return Number.isInteger(v) ? v : Number(v.toFixed(1));
        },
        statusChip(r){
            switch (r.status){
                case 'passed': return {color: 'success', text: 'ناجح', pct: this.formatPct(r.percentage)};
                case 'failed': return {color: 'error', text: 'راسب', pct: this.formatPct(r.percentage)};
                case 'pending': return {color: 'orange', text: 'بانتظار التصحيح', pct: null};
                default: return {color: 'grey', text: 'تم التسليم', pct: null};
            }
        },
        alertColor(type){
            return type === 'late_homework' ? 'error' : 'orange darken-3';
        },
        attentionIcon(type){
            return type === 'late_homework' ? 'mdi-clock-alert-outline' : 'mdi-lock-alert-outline';
        },
        courseLines(c){
            return [
                {label: 'محاضرات', done: c.lectures_seen || 0, total: c.lectures_total || 0},
                {label: 'فيديوهات', done: c.videos_seen || 0, total: c.videos_total || 0},
                {label: 'امتحانات وواجبات', done: c.exams_done || 0, total: c.exams_total || 0},
            ];
        },
    },
    created(){
        // Parent-only page: mark <html> so the unscoped style below hides the app shell while this page is mounted.
        document.documentElement.classList.add('parent-report-mode');
        if (!this.enabled) return;
        let stored = null;
        try { stored = window.localStorage.getItem(this.storageKey); } catch (e) { stored = null; }
        if (stored){
            this.loadReport(stored);
        }else{
            this.checkLink();
        }
    },
}
</script>

<style lang="scss">
/* Shell pieces live in App.vue; this page hides them for the parent without touching that file. */
html.parent-report-mode .navbar.v-app-bar,
html.parent-report-mode .app-menu,
html.parent-report-mode .v-bottom-navigation,
html.parent-report-mode .fixed-top-bar{
    display: none!important;
}
html.parent-report-mode .v-main{
    padding: 0!important;
}
</style>

<style lang="scss" scoped>
.report-container{
    max-width: 960px;
}
.state-text{
    font-size: 1.05rem;
    line-height: 1.9;
}
.section-title{
    font-size: 1.1rem;
    font-weight: 700;
}
.stat-tile{
    border-radius: 10px!important;
}
.stat-value{
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.3;
}
.average-value{
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.2;
}
.student-name{
    line-height: 1.5;
    word-break: break-word;
}
.min-w-0{
    min-width: 0;
}
.result-row.with-border{
    border-top: 1px solid rgba(0,0,0, .08);
}
.chips-gap{
    gap: 6px;
}
.course-header{
    min-height: 56px;
}
.logout-btn{
    min-height: 44px!important;
}
.v-application .parent-report .num, .code-input ::v-deep input{
    font-family: "open sans", Arial, sans-serif!important;
}
.code-input ::v-deep input{
    text-align: center;
    direction: ltr;
    font-size: 1.6rem;
    letter-spacing: 0.5em;
    font-weight: 600;
}
</style>
